import React, { ReactNode, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import { useMutation, useQuery } from '@tanstack/react-query'
import WinbackAPI from 'api/faber/WinbackAPI'
import MenuClose from 'assets/icons/MenuClose.png'
import { RoundedButton } from 'common/Button/Button'
import OrbitSession from 'hooks/useCookies'
import { SubscriptionsTableRows } from 'types/api/faber/subscription.type'

import WinbackConfirmDialog from './WinbackConfirmDialog'
import WinbackSuccessDialog from './WinbackSuccessDialog'

interface WinbackDialogProps {
  handleWinbackDialogOpen: (value: void) => void
  handleWinbackDialogClose: (value: void) => void
  winbackDialog: boolean
  customerRow: SubscriptionsTableRows
}

const WinbackDialog = ({
  handleWinbackDialogOpen,
  handleWinbackDialogClose,

  winbackDialog,
  customerRow
}: WinbackDialogProps) => {
  const faberToken = OrbitSession.get('faberToken')
  const [showWinbackConfirmDialog, setShowWinbackConfirmDialog] =
    useState(false)
  const [showWinbackSuccessDialog, setShowWinbackSuccessDialog] =
    useState(false)

  const resetDialog = () => {
    setCredits('')
    setWinback('')
    setBonusData('')
    setIndex('')
    setTotalBonusDataAmount('')
    setWaiverValue(0)
    setMonths(0)
    setShowData(false)
    setShowHotelCredits(false)
    setShowFeeWaiver(false)
  }

  const resetWinbackFormChoice = () => {
    setCredits('')
    setMonths(0)
    setWaiverValue(0)
    setBonusData('')
    setIndex('')
  }

  const handleDialogClose = () => {
    handleWinbackDialogClose()
    setShowWinbackConfirmDialog(false)
    setShowWinbackSuccessDialog(false)
    resetDialog()
  }

  function handleApply() {
    setShowWinbackConfirmDialog(true)
    handleWinbackDialogClose()
  }

  function handleBack() {
    resetDialog()
    setShowWinbackConfirmDialog(false)
    setShowWinbackSuccessDialog(false)
    handleWinbackDialogOpen()
    setWinbackSummary('')
    setWinbackTotalAmount('')
  }

  const [cancelled, setCancelled] = useState(false)
  const [proPac, setProPac] = useState(false)

  const [winbackSummary, setWinbackSummary] = useState('')
  const [winbackTotalAmount, setWinbackTotalAmount] = useState('')

  const [showData, setShowData] = useState(false)
  const [showHotelCredits, setShowHotelCredits] = useState(false)
  const [showFeeWaiver, setShowFeeWaiver] = useState(false)

  const [winback, setWinback] = useState('')
  const [bonusData, setBonusData] = useState('')

  const [index, setIndex] = useState('')
  const [credits, setCredits] = useState('')
  const [totalBonusDataAmount, setTotalBonusDataAmount] = useState('')

  const [waiverValue, setWaiverValue] = useState(0)
  const [months, setMonths] = useState(0)
  const [waiverError, setWaiverError] = useState({
    errorOne: true,
    errorTwo: true
  })
  const { errorOne, errorTwo } = waiverError

  const [waiverErrorMessage, setWaiverErrorMessage] = useState('')

  const [initialVoucherValue, setInitialVoucherValue] = useState(0)

  //winback options
  const handleChange = (event: SelectChangeEvent) => {
    switch (event.target.value) {
      case 'Bonus data':
        setShowData(true)
        setShowHotelCredits(false)
        setShowFeeWaiver(false)
        setWinback(event.target.value)
        resetWinbackFormChoice()

        break

      case 'Hotel credits':
        setShowHotelCredits(true)
        setShowData(false)
        setShowFeeWaiver(false)
        setWinback(event.target.value)
        setWinbackSummary('Hotel credits')
        resetWinbackFormChoice()

        break

      case 'Bill waiver':
        setShowFeeWaiver(true)
        setShowHotelCredits(false)
        setShowData(false)
        setWinback(event.target.value)
        setWinbackSummary('Bill Waiver')
        resetWinbackFormChoice()

        break
    }
  }

  //bonus data functions
  const { data: getAllWinbackPlans } = useQuery(
    ['getAllWinbackPlans'],
    async () => {
      const { data } = await WinbackAPI.getBonusDataPlans(faberToken)
      return data
    }
  )

  //bonus data api
  const { mutate: updateBonusData, isLoading: loading } = useMutation(
    WinbackAPI.updateBonusDataPlans,
    {
      onSuccess: () => {
        toast.success('Bonus data allocated')
        setShowWinbackSuccessDialog(true)
        setShowWinbackConfirmDialog(false)
      },
      onError: () => {
        toast.error('Error allocating bonus data')
      }
    }
  )

  function updateTotalData() {
    updateBonusData({
      userID: customerRow.userID,
      packageTypeID: bonusData,
      remarks: 'Data Winback by CH',
      faberToken: faberToken
    })
  }

  const mapIndex = {
    0: String(getAllWinbackPlans?.data[0].packageTypeID),
    1: String(getAllWinbackPlans?.data[1].packageTypeID)
  }

  //voucher functions
  interface topUpCreditsType {
    20: ReactNode
    40: ReactNode
    60: ReactNode
  }

  const topUpCredits: topUpCreditsType = {
    20: (
      <Select
        labelId='credit-optioms'
        id='credit-options'
        value={credits}
        fullWidth={true}
        onChange={handleChange}
        label='Hotel credits'
        sx={{ marginBottom: '24.5px' }}
      >
        <MenuItem
          value={20}
          onClick={() => {
            setCredits('20')
            setWinbackTotalAmount('$20')
          }}
        >
          20
        </MenuItem>
      </Select>
    ),
    40: (
      <Select
        labelId='credit-optioms'
        id='credit-options'
        value={credits}
        fullWidth={true}
        onChange={handleChange}
        label='Hotel credits'
        sx={{ marginBottom: '24.5px' }}
      >
        <MenuItem
          value={20}
          onClick={() => {
            setCredits('20')
            setWinbackTotalAmount('$20')
          }}
        >
          20
        </MenuItem>
        <MenuItem
          value={40}
          onClick={() => {
            setCredits('40')
            setWinbackTotalAmount('$40')
          }}
        >
          40
        </MenuItem>
      </Select>
    ),
    60: (
      <Select
        labelId='credit-optioms'
        id='credit-options'
        value={credits}
        fullWidth={true}
        onChange={handleChange}
        label='Hotel credits'
        sx={{ marginBottom: '24.5px' }}
      >
        <MenuItem
          value={20}
          onClick={() => {
            setCredits('20')
            setWinbackTotalAmount('$20')
          }}
        >
          20
        </MenuItem>
        <MenuItem
          value={40}
          onClick={() => {
            setCredits('40')
            setWinbackTotalAmount('$40')
          }}
        >
          40
        </MenuItem>
        <MenuItem
          value={60}
          onClick={() => {
            setCredits('60')
            setWinbackTotalAmount('$60')
          }}
        >
          60
        </MenuItem>
      </Select>
    )
  }

  const topUp = 60 - initialVoucherValue
  const newBalance = Number(initialVoucherValue) + Number(credits)

  //vouchers APIs
  const { mutate: mutateGetVouchers } = useMutation(
    WinbackAPI.hotelCreditsWinback,
    {
      onSuccess: (res) => {
        setInitialVoucherValue(res.data.data[0].value)
      },
      onError: () => {
        toast.error('User plan has no vouchers')
        handleDialogClose()
      }
    }
  )

  const { mutate: updateVouchers } = useMutation(
    WinbackAPI.updateHotelCreditsWinback,
    {
      onSuccess: () => {
        toast.success('Updated voucher')
        setShowWinbackSuccessDialog(true)
        setShowWinbackConfirmDialog(false)
      },
      onError: () => {
        toast.error('Error updating user vouchers')
      }
    }
  )

  function getUserVouchers() {
    mutateGetVouchers({
      userId: customerRow.userID,
      faberToken: faberToken
    })
  }

  function updateUserVouchers() {
    updateVouchers({
      userId: customerRow.userID,
      voucherCreditChange: Number(credits),
      faberToken: faberToken
    })
  }

  function onCancelClick() {
    setShowWinbackConfirmDialog(false)
    setShowWinbackSuccessDialog(false)
    handleWinbackDialogClose()

    resetDialog()
  }

  function handleConfirm() {
    if (showHotelCredits) {
      updateUserVouchers()
    } else if (bonusData) {
      updateTotalData()
    } else if (showFeeWaiver) {
      updateTotalFeeWaiver()
    }
  }

  useEffect(() => {
    if (customerRow.planStatus === 'CANCELLED') {
      setCancelled(true)
    }
    if (customerRow.planName === 'Lite Pac') {
      setProPac(false)
    }
    if (
      customerRow.planName === 'Pro Pac' &&
      customerRow.planStatus !== 'CANCELLED'
    ) {
      setProPac(true)
    }
  }, [])

  //BILL WAIVER
  const handleBillWaiverAmountInput = (
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = evt.target.value
    const regex = /^[0-9]+$/
    if (newValue.match(regex)) {
      setWaiverError({ ...waiverError, errorOne: false })
      if (
        Number(newValue) >
        (getUserWaivers?.data.planName === 'Pro Pac' ? 25 : 10)
      ) {
        setWaiverError({ ...waiverError, errorOne: true })
        setWaiverErrorMessage(
          `Cap of SGD ${
            getUserWaivers?.data.planName === 'Pro Pac' ? 25 : 10
          } Waiver for ${getUserWaivers?.data.planName}`
        )
        setWaiverValue(0)
      } else {
        setWaiverErrorMessage('')
        setWaiverError({ ...waiverError, errorOne: false })
        setWaiverValue(Number(newValue))
        setWinbackTotalAmount(`$${String(Number(newValue) * months)}`)
      }
    } else {
      setWaiverError({ ...waiverError, errorOne: true })
      setWaiverValue(0)
    }
  }

  const handleBillCyclesInput = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = evt.target.value
    const regex = /^[0-9]+$/
    if (newValue.match(regex) && Number(newValue) > 0) {
      setWaiverError({ ...waiverError, errorTwo: false })
      setMonths(Number(newValue))
      setWinbackTotalAmount(`$${String(Number(newValue) * waiverValue)}`)
    } else {
      setWaiverError({ ...waiverError, errorTwo: true })
      setMonths(0)
    }
  }

  const { data: getUserWaivers, refetch: refetchGetUserWaivers } = useQuery(
    ['getUserWaivers'],
    async () => {
      const { data } = await WinbackAPI.getUserPlans(
        faberToken,
        customerRow.userID
      )
      return data
    }
  )

  const { mutate: updateWaiver } = useMutation(WinbackAPI.provisionWaiver, {
    onSuccess: () => {
      toast.success('Updated Waiver')
      setShowWinbackSuccessDialog(true)
      setShowWinbackConfirmDialog(false)
    },
    onError: () => {
      toast.error('User already has a waiver applied')
      setShowWinbackConfirmDialog(false)
      setShowWinbackConfirmDialog(false)
    }
  })

  function updateTotalFeeWaiver() {
    updateWaiver({
      userID: customerRow.userID,
      currency: getUserWaivers?.data.currency,
      waiverValue: waiverValue,
      planName: getUserWaivers?.data.planName,
      months: months,
      faberToken: faberToken
    })
  }

  useEffect(() => {
    refetchGetUserWaivers()
  }, [customerRow])

  return (
    <>
      <Dialog
        open={winbackDialog}
        onClose={() => {
          handleDialogClose()
        }}
        maxWidth='lg'
      >
        <DialogTitle sx={{ padding: '24px', margin: '0px' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: '-15px',
              mt: '-5px'
            }}
          >
            <Typography
              sx={{ color: 'black', fontSize: '24px', fontWeight: '700' }}
            >
              Customer Winback
            </Typography>
            <IconButton
              onClick={() => {
                handleDialogClose()
              }}
              size='small'
              data-cy='closeDialogBtn'
              sx={{
                mr: '-10px',
                mt: '-5px',
                width: '40px',
                height: '40px'
              }}
            >
              <img
                style={{
                  width: '20px',
                  height: 'auto'
                }}
                src={MenuClose}
              />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: '384px',
            minHeight: '500px'
          }}
        >
          <Typography
            sx={{ color: '#333333', fontSize: '18px', fontWeight: 500 }}
          >
            Select a winback
          </Typography>
          <Typography sx={{ color: '#818D9C', fontSize: '14px' }}>
            Winback
          </Typography>
          <FormControl variant='standard'>
            <InputLabel id='winback-options' sx={{ pl: '4px' }}>
              Select winback
            </InputLabel>
            <Select
              labelId='bonus-data'
              id='bonus-data-options'
              value={winback}
              fullWidth={true}
              onChange={handleChange}
              label='Select winback'
              sx={{ marginBottom: '24.5px', pl: '4px' }}
            >
              {!cancelled && <MenuItem value='Bonus data'>Bonus data</MenuItem>}
              {proPac && !cancelled && (
                <MenuItem value='Hotel credits' onClick={getUserVouchers}>
                  Hotel credits
                </MenuItem>
              )}
              {!cancelled && (
                <MenuItem value='Bill waiver'>Bill waiver</MenuItem>
              )}
            </Select>
          </FormControl>
          {showData && (
            <>
              <Stack>
                <Typography
                  sx={{
                    fontWeight: '700',
                    marginBottom: '-8px',
                    fontSize: '18px',
                    color: '#333333'
                  }}
                >
                  Set data amount
                </Typography>
                <Typography
                  sx={{
                    color: '#818D9C',
                    marginTop: '0px',
                    marginBottom: '20px',
                    fontSize: '16px'
                  }}
                >
                  (Expiry: 30 days)
                </Typography>
                <Typography sx={{ color: '#818D9C', marginTop: '0px' }}>
                  Bonus data
                </Typography>
              </Stack>

              <FormControl variant='standard'>
                <InputLabel id='plan-options' sx={{ pl: '4px' }}>
                  Select plan
                </InputLabel>

                <Select
                  labelId='bonus-data'
                  id='bonus-data-options'
                  value={index}
                  fullWidth={true}
                  onChange={(event) => {
                    setIndex(event.target.value)
                    setTotalBonusDataAmount(
                      getAllWinbackPlans?.data[event.target.value].planName
                    )
                    setBonusData(mapIndex[event.target.value])
                    setWinbackSummary(
                      `${getAllWinbackPlans?.data[event.target.value].planName
                        .split(' ')
                        .pop()} bonus data`
                    )
                    setWinbackTotalAmount(
                      getAllWinbackPlans?.data[event.target.value].planName
                        .split(' ')
                        .pop()
                    )
                  }}
                  label='Bonus data'
                  sx={{ marginBottom: '24.5px' }}
                >
                  {getAllWinbackPlans?.data.map((plan, index) => (
                    <MenuItem key={plan.ID} value={index}>
                      <Typography>{plan.planName.split(' ').pop()}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Stack
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between'
                }}
              >
                <Typography
                  sx={{ fontWeight: '700', fontSize: '18px', color: '#151D29' }}
                >
                  Total amount:
                </Typography>
                <Typography
                  sx={{
                    display: 'inline',
                    fontWeight: '700',
                    color: '#151D29',
                    fontSize: '18px'
                  }}
                >
                  {totalBonusDataAmount.split(' ').pop()}
                </Typography>
              </Stack>
            </>
          )}
          {showHotelCredits && (
            <>
              <Stack>
                <Typography
                  sx={{
                    fontWeight: '700',
                    marginBottom: '-5px',
                    fontSize: '18px'
                  }}
                >
                  Select credits amount
                </Typography>
                <Stack
                  sx={{ flexDirection: 'row', justifyContent: 'space-between' }}
                >
                  <Typography sx={{ color: '#818D9C', marginTop: '0px' }}>
                    Credits(SGD)
                  </Typography>
                  <Stack sx={{ flexDirection: 'column', textAlign: 'right' }}>
                    <Typography sx={{ color: '#818D9C', marginTop: '0px' }}>
                      <>Current balance: {initialVoucherValue}/60</>
                    </Typography>
                    <Typography sx={{ marginTop: '-10px' }}>
                      <>New balance: {newBalance}/60</>
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>

              <FormControl variant='standard'>
                <InputLabel id='hotel-credits-options'>
                  Select credits
                </InputLabel>
                {topUpCredits[topUp as keyof topUpCreditsType]}
              </FormControl>
            </>
          )}

          {showFeeWaiver && (
            <>
              <Stack>
                <Typography
                  sx={{
                    fontWeight: '700',
                    marginBottom: '-8px',
                    fontSize: '18px',
                    color: '#151D29'
                  }}
                >
                  Select bill waiver amount
                </Typography>
                <Typography
                  sx={{
                    marginTop: '0px',
                    marginBottom: '20px',
                    fontSize: '16px',
                    color: '#151D29'
                  }}
                >
                  (To be reflected on next month’s plan:
                  <span style={{ fontWeight: 700 }}>
                    {getUserWaivers?.data.planName})
                  </span>
                </Typography>
              </Stack>
              <Typography
                sx={{ color: 'rgba(129, 141, 156, 1)', fontSize: '12px' }}
              >
                {' '}
                Waiver Amount (Cap: $
                {getUserWaivers?.data.planName === 'Pro Pac' ? 25 : 10}{' '}
                {getUserWaivers?.data.currency})
              </Typography>
              <FormControl>
                <TextField
                  variant='standard'
                  label='Whole numbers only, no decimals'
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        {getUserWaivers?.data.currency}
                      </InputAdornment>
                    )
                  }}
                  onChange={handleBillWaiverAmountInput}
                  error={errorOne}
                  helperText={waiverErrorMessage}
                />

                <Typography
                  sx={{
                    fontWeight: '700',
                    fontSize: '18px',
                    color: '#151D29',
                    mt: '22px'
                  }}
                >
                  Select number of billing cycles
                </Typography>
                <Typography
                  sx={{
                    marginTop: '-8px',
                    fontSize: '16px',
                    color: '#151D29'
                  }}
                >
                  (To take effect from next billing cycle)
                </Typography>
                <TextField
                  variant='standard'
                  label='Whole numbers only, no decimals'
                  error={errorTwo}
                  onChange={handleBillCyclesInput}
                />
              </FormControl>
            </>
          )}
        </DialogContent>
        <Stack
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            margin: '50px 16px 20px 0'
          }}
        >
          <RoundedButton
            sx={{ padding: '12px 24px' }}
            onClick={() => {
              handleDialogClose()
            }}
          >
            Cancel
          </RoundedButton>

          <RoundedButton
            variant='filled'
            disabled={
              !((!!months && !!waiverValue) || !!credits || !!bonusData)
            }
            onClick={handleApply}
            sx={{
              padding: '12px 24px',
              '&.Mui-disabled': {
                backgroundColor: 'rgba(207, 216, 227, 1)'
              }
            }}
          >
            Apply
          </RoundedButton>
        </Stack>
      </Dialog>
      {showWinbackConfirmDialog && (
        <WinbackConfirmDialog
          handleBack={handleBack}
          showWinbackConfirmDialog={showWinbackConfirmDialog}
          handleDialogClose={handleDialogClose}
          onCancelClick={onCancelClick}
          handleConfirm={handleConfirm}
          winbackSummary={winbackSummary}
          winbackTotalAmount={winbackTotalAmount}
          loading={loading}
          months={months}
          title={winback}
        />
      )}
      {showWinbackSuccessDialog && (
        <WinbackSuccessDialog
          showWinbackSuccessDialog={showWinbackSuccessDialog}
          handleDialogClose={handleDialogClose}
          title={winback}
        />
      )}
    </>
  )
}
export default WinbackDialog
